import { isEqual, mean, uniq } from 'es-toolkit';
import { produce } from 'immer';

import { CalculatedLine, Component, Member } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { hasAutoCutOnOverlapFeatureFlag } from '@paragon/login-tools';
import { calculateLine, calculateRelativeMemberGeometry, cutAgainst } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, circularPairwise, sortBy } from '@paragon/utilities';

import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { toggleMemberCuttingMode } from '../slices/applicationSlice';
import { setMemberInComponentDesign, setMembers } from '../slices/componentDesignsSlice';
import { updateFeatures } from './appActions';
import { ThunkActionVoid } from './thunkActions';

function notEqualTo<T>(value: T) {
  return (other: T) => value !== other;
}

/**
 * Makes (immutably) editing a member's cuts easier. Returns the edited member and a boolean indicating whether the
 *   member's cuts (not necessarily its resulting geometry) were actually changed.
 */
function withEditedCuts(
  member: Member,
  side: 'base' | 'end',
  callback: (referenceLineGuids: string[]) => string[],
): [Member, boolean] {
  if (member.relativeGeometry == null) {
    throw new Error('Member must have relative geometry to be cut.');
  }

  let changesMade = false;

  const editedMember = produce(member, (draft) => {
    switch (side) {
      case 'base':
        draft.relativeGeometry!.baseCutGuids = callback(draft.relativeGeometry!.baseCutGuids);
        changesMade = !isEqual(draft.relativeGeometry!.baseCutGuids, member.relativeGeometry!.baseCutGuids);
        break;

      case 'end':
        draft.relativeGeometry!.endCutGuids = callback(draft.relativeGeometry!.endCutGuids);
        changesMade = !isEqual(draft.relativeGeometry!.endCutGuids, member.relativeGeometry!.endCutGuids);
        break;

      default:
        assertExhaustiveSwitch(side);
    }
  });

  return [editedMember, changesMade];
}

/**
 * Represents a change that could be made to a member's cuts. Can be fluently created using the `addCut`, `removeCut`,
 *   and `noop` functions found below. Apply to a member using `applyCutDiff`.
 */
type CutDiff =
  | { type: 'noop' }
  | { type: 'added'; end: 'base' | 'end'; referenceLineGuid: string }
  | { type: 'removed'; end: 'base' | 'end'; referenceLineGuid: string };

function addCut(end: 'base' | 'end', referenceLineGuid: string): CutDiff {
  return { type: 'added', end, referenceLineGuid };
}

function removeCut(end: 'base' | 'end', referenceLineGuid: string): CutDiff {
  return { type: 'removed', end, referenceLineGuid };
}

function noop(): CutDiff {
  return { type: 'noop' };
}

/**
 * Applies a `CutDiff` to a `Member`, returning the edited member and the diff that was actually applied (a diff will
 *   not be applied if it would have no effect).
 */
function applyCutDiff(member: Member, diff: CutDiff): [Member, CutDiff] {
  let changed = false;

  switch (diff.type) {
    case 'noop':
      return [member, diff];

    case 'added':
      [member, changed] = withEditedCuts(member, diff.end, (cuts) => uniq(cuts.concat(diff.referenceLineGuid)));
      break;

    case 'removed':
      [member, changed] = withEditedCuts(member, diff.end, (cuts) => cuts.filter(notEqualTo(diff.referenceLineGuid)));
      break;

    default:
      assertExhaustiveSwitch(diff);
  }

  return [member, changed ? diff : noop()];
}

/**
 * This class contains methods for manipulating the hypothetical result(s) of cutting or uncutting members. Designed
 *   with the use case in mind of stringing together multiple (and potentially branching) hypothetical cuts, then
 *   determining the best end result to actually use.
 *
 * For example, to dynamically determine which end of a member to add a cut to, you could call `ifCutWasAdded` with
 *   `{ to: 'both-sides' }`, then check which result best suits your needs.
 *
 * Primary methods are `ifCutWasAdded` and `ifCutWasRemoved`, both of which return a `HypotheticalCutMemberCollection`
 *   to allow for multiple branches of edits to be considered. To obtain a `HypotheticalCutMember` from a real
 *   (non -hypothetical) member, use the static method `representing`, or `fromEditing` if you already have a change to
 *   apply.
 */
class HypotheticalCutMember {
  private readonly diff: CutDiff;
  private readonly parent?: HypotheticalCutMember;

  public readonly member: Member;
  private readonly component: Component;

  private geometry: ReturnType<typeof calculateRelativeMemberGeometry> | 'not-yet-calculated';

  constructor(member: Member, parent: Component | HypotheticalCutMember, diff: CutDiff) {
    this.diff = diff;
    this.member = member;
    this.geometry = 'not-yet-calculated';

    if (parent instanceof HypotheticalCutMember) {
      this.parent = parent;
      this.component = parent.component;
    } else {
      this.component = parent;
    }

    if (member.relativeGeometry == null) {
      throw new Error('Member must have relative geometry to be cut.');
    }
  }

  static representing(member: Member, component: Component): HypotheticalCutMember {
    return new HypotheticalCutMember(member, component, noop());
  }

  static fromEditing(parent: HypotheticalCutMember, diff: CutDiff): HypotheticalCutMember {
    const [editedMember, appliedDiff] = applyCutDiff(parent.member, diff);
    return new HypotheticalCutMember(editedMember, parent, appliedDiff);
  }

  get affectedEnd(): 'base' | 'end' | null {
    switch (this.diff.type) {
      case 'noop':
        return null;

      case 'added':
      case 'removed':
        return this.diff.end;

      default:
        assertExhaustiveSwitch(this.diff);
    }
  }

  addedCut(): boolean {
    return this.diff.type === 'added';
  }

  /**
   * If this hypothetical edit involves adding a cut, calculates and returns the segment of the member produced by the
   *   added cut. Likely to throw an exception otherwise, so call with caution.
   */
  calculateAddedCutSegment(): LineSegment2D | null {
    if (this.diff.type !== 'added') throw new Error('Geometry can only be caculated for an added cut.');

    const referenceLineGuid = this.diff.referenceLineGuid;
    const referenceLine = (this.component.referenceLines ?? []).find((line) => line.guid === referenceLineGuid);
    if (referenceLine == null) throw new Error('Component does not contain reference line used to cut member.');

    const calculatedCutLine = calculateLine(referenceLine, this.component);
    if (calculatedCutLine == null) return null;

    const memberGeometry = this.getOrCalculateGeometry();
    if (memberGeometry == null) return null;

    return (
      circularPairwise(memberGeometry.geometry)
        .map(LineSegment2D.fromPointArray)
        .find(calculatedCutLine.containsSegment.bind(calculatedCutLine)) ?? null
    );
  }

  /**
   * Produces the hypothetical member(s) that would result from adding a cut to one or both sides of this member.
   */
  ifCutWasAdded(
    referenceLineGuid: string,
    options?: { to?: 'base' | 'end' | 'both-sides' },
  ): HypotheticalCutMemberCollection {
    switch (options?.to) {
      case 'base':
      case 'end':
        return onlyOneEdit(HypotheticalCutMember.fromEditing(this, addCut(options.to, referenceLineGuid)));

      case undefined:
      case 'both-sides':
        return multiplePotentialEdits(
          this.ifCutWasAdded(referenceLineGuid, { to: 'base' }),
          this.ifCutWasAdded(referenceLineGuid, { to: 'end' }),
        );
    }
  }

  /**
   * Produces the hypothetical member(s) that would result from removing a cut from one or both sides of this member.
   */
  ifCutWasRemoved(
    referenceLineGuid: string,
    options?: { from?: 'base' | 'end' | 'both-sides' },
  ): HypotheticalCutMemberCollection {
    switch (options?.from) {
      case 'base':
      case 'end':
        return onlyOneEdit(HypotheticalCutMember.fromEditing(this, removeCut(options.from, referenceLineGuid)));

      case undefined:
      case 'both-sides':
        return multiplePotentialEdits(
          this.ifCutWasRemoved(referenceLineGuid, { from: 'base' }),
          this.ifCutWasRemoved(referenceLineGuid, { from: 'end' }),
        );
    }
  }

  /**
   * Determines if any cuts are redundant (do not affect the member's actual geometry), and returns the hypothetical
   *   member that would result if they were removed.
   */
  garbageCollect(): HypotheticalCutMember {
    let result: HypotheticalCutMember = this; // eslint-disable-line @typescript-eslint/no-this-alias

    let scenarios;

    do {
      scenarios = multiplePotentialEdits(
        ...result
          .getAllCuttingReferenceLineGuids()
          .map((referenceLineGuid) => result.ifCutWasRemoved(referenceLineGuid, { from: 'both-sides' })),
      ).filter((option) => option.wasEdited() && option.isValid() && option.preservesOriginalShape());

      result = scenarios.first() ?? result;
    } while (scenarios.any());

    return result;
  }

  private getOrCalculateGeometry(): ReturnType<typeof calculateRelativeMemberGeometry> | null {
    if (this.geometry === 'not-yet-calculated') {
      this.geometry = calculateRelativeMemberGeometry(this.member.relativeGeometry!, this.component);
    }

    return this.geometry;
  }

  /**
   * Returns `false` if valid polygon cannot be calculated for the member (after hypothetical changes are made). This
   *   method is cached, so it is safe to call frequently.
   */
  isValid(): boolean {
    return this.getOrCalculateGeometry() != null;
  }

  /**
   * Returns the underlying member's geometry (with hypothetical changes made) as a polygon, or `null` if the member's
   *   geometry is invalid. This method is cached, so it is safe to call frequently.
   */
  asPolygon(): Polygon2D | null {
    const geometry = this.getOrCalculateGeometry();
    return geometry ? Polygon2D.fromVerticesIfValid(geometry.geometry) : null;
  }

  /**
   * Returns the midline of the underlying member's geometry (with hypothetical changes made), or `null` if the member's
   *    geometry is invalid. This method is cached, so it is safe to call frequently.
   */
  getMidline(): LineSegment2D | null {
    const geometry = this.getOrCalculateGeometry();
    return geometry ? geometry.midline : null;
  }

  /**
   * Whether or not the member's cuts were actually changed by the hypothetical cut operation.
   *
   *   - Only considers whether a change was made to this instance of `HypotheticalCutMember` -- if based on another
   *       `HypotheticalCutMember`, it will not consider whether any cuts were previously changed.
   *   - Only considers whether the cuts were changed, not whether the member's geometry was actually changed. See
   *       `actuallyChangedShape` for that use case.
   */
  wasEdited(): boolean {
    return this.diff.type !== 'noop';
  }

  /**
   * Whether or not the member's geometry was actually changed by the hypothetical cut operation.
   *  - Only considers this specific instance of `HypotheticalCutMember` -- if based on previous hypothetical cuts, the
   *      underlying member's geometry may still be different compared to the original member.
   */
  actuallyChangedShape(): boolean {
    if (this.parent == null) return false;
    if (this.diff.type === 'noop') return false;

    if (this.isValid()) {
      const previousPolygon = this.parent.asPolygon();
      const currentPolygon = this.asPolygon();

      return previousPolygon && currentPolygon
        ? !previousPolygon.equals(currentPolygon)
        : previousPolygon !== currentPolygon;
    } else {
      return !this.parent.isValid();
    }
  }

  /**
   * The inverse of `actuallyChangedShape`; see that method for details.
   */
  preservesOriginalShape(): boolean {
    return !this.actuallyChangedShape();
  }

  /**
   * Returns all reference line GUIDs that would cut the underlying member (after hypothetical changes from this
   *   instance and any parent changes have been).
   */
  getAllCuttingReferenceLineGuids(): string[] {
    return uniq([
      ...(this.member.relativeGeometry?.baseCutGuids ?? []),
      ...(this.member.relativeGeometry?.endCutGuids ?? []),
    ]);
  }

  /**
   * Whether, as of this instance, the underlying member is (actually or hypothetically) cut by a given reference line.
   */
  isCutBy(referenceLineGuid: string): boolean {
    return this.getAllCuttingReferenceLineGuids().includes(referenceLineGuid);
  }
}

/**
 * A fluent method to create a `HypotheticalCutMemberCollection` with a single hypothetical edit.
 */
function onlyOneEdit(editedMember: HypotheticalCutMember): HypotheticalCutMemberCollection {
  return new HypotheticalCutMemberCollection([editedMember]);
}

/**
 * A fluent method to create a `HypotheticalCutMemberCollection` with multiple hypothetical edits.
 */
function multiplePotentialEdits(
  ...sources: (HypotheticalCutMember | HypotheticalCutMemberCollection)[]
): HypotheticalCutMemberCollection {
  return new HypotheticalCutMemberCollection(
    sources.flatMap((item) => (item instanceof HypotheticalCutMemberCollection ? item.toArray() : item)),
  );
}

/**
 * Represents a collection of hypothetical edits to a member's cuts. Contains methods for working with, evaluating, and
 *   further manipulating the hypothetical edits in the collection.
 */
class HypotheticalCutMemberCollection {
  private members: HypotheticalCutMember[];

  constructor(members: HypotheticalCutMember[]) {
    this.members = members;
  }

  first(): HypotheticalCutMember | null {
    return this.members[0] ?? null;
  }

  single(): HypotheticalCutMember {
    if (this.members.length !== 1) {
      throw new Error('Expected exactly one member in collection.');
    }

    return this.members[0];
  }

  any(): boolean {
    return this.members.length > 0;
  }

  noneRemaining() {
    return this.members.length === 0;
  }

  allAffectSameEnd(): boolean {
    return (
      this.members.length === 0 || this.members.every((member) => member.affectedEnd === this.first()!.affectedEnd)
    );
  }

  map(
    callback: (member: HypotheticalCutMember) => HypotheticalCutMember | HypotheticalCutMemberCollection,
  ): HypotheticalCutMemberCollection {
    return multiplePotentialEdits(...this.members.map(callback));
  }

  filter(callback: (member: HypotheticalCutMember) => boolean): HypotheticalCutMemberCollection {
    return new HypotheticalCutMemberCollection(this.members.filter(callback));
  }

  orderBy(callback: (member: HypotheticalCutMember) => any, order?: 'asc' | 'desc'): HypotheticalCutMemberCollection {
    return new HypotheticalCutMemberCollection(sortBy(this.members, callback, order));
  }

  some(callback: (member: HypotheticalCutMember) => boolean): boolean {
    return this.members.some(callback);
  }

  none(callback: (member: HypotheticalCutMember) => boolean): boolean {
    return !this.some(callback);
  }

  every(callback: (member: HypotheticalCutMember) => boolean): boolean {
    return this.members.every(callback);
  }

  toArray() {
    return this.members.slice();
  }

  /**
   * Produces the hypothetical member(s) that would result from adding a cut to one or both sides of each of the
   *   hypothetical member scenarios in this collection.
   */
  andCutWasAdded(
    referenceLineGuid: string,
    options?: { to?: 'base' | 'end' | 'both-sides' },
  ): HypotheticalCutMemberCollection {
    return this.map((option) => option.ifCutWasAdded(referenceLineGuid, options));
  }

  /**
   * Produces the hypothetical member(s) that would result from removing a cut from one or both sides of each of the
   *   hypothetical member scenarios in this collection.
   */
  andCutWasRemoved(
    referenceLineGuid: string,
    options?: { from?: 'base' | 'end' | 'both-sides' },
  ): HypotheticalCutMemberCollection {
    return this.map((option) => option.ifCutWasRemoved(referenceLineGuid, options));
  }

  /**
   * Attempts to determine the best hypothetical cut (or chain of cuts/removals) to apply, based on the options present
   *   in this collection. If no valid options are present, returns `null`.
   *
   * This method is designed to be used in the context of a user clicking on a reference line to cut a member. The
   *   huristics are specialized to that use case, and it's probably not suitable for any other use cases. Left in this
   *   class for convenience's sake, but feel free to move it elsewhere in the future.
   */
  pickBestOption(options: { disambiguatingLine: Line2D; disambiguatingPoint: Point2D }): HypotheticalCutMember | null {
    let seriousOptions = this.filter((option) => option.wasEdited() && option.isValid());
    if (seriousOptions.noneRemaining()) return null;

    // After filtering out invalid options we'll only be left with a few choices (for this very specific use case, which
    //   is why this method is not suitable for general-purpose use).

    // A huristic for cases where the correct end to cut is ambiguous. Picking the longest result is a good bet since
    //   cutting the wrong end typically causes a very short and clearly wrong result.
    if (!seriousOptions.allAffectSameEnd()) {
      const baseEdits = seriousOptions.filter((option) => option.affectedEnd === 'base');
      const endEdits = seriousOptions.filter((option) => option.affectedEnd === 'end');

      const meanBaseCutResultingLength = mean(baseEdits.members.map((option) => option.getMidline()!.length));
      const meanEndCutResultingLength = mean(endEdits.members.map((option) => option.getMidline()!.length));

      seriousOptions = meanBaseCutResultingLength > meanEndCutResultingLength ? baseEdits : endEdits;
    }

    if (seriousOptions.members.length === 1) return seriousOptions.single();

    // The other huristic; to make "additive" cutting work when the correct old cut to remove is ambiguous. We rely on a
    //  disambiguating point and line here, which are the reference line that was actually clicked and the exact mouse
    //  position of the click (not necessarily precisely on the line). The "correct" cut to remove is probably the one
    //   on the same side of the member as the user's mouse.
    if (seriousOptions.every((option) => option.addedCut())) {
      const projectedDisambiguatingPoint = options.disambiguatingPoint.projectOntoLine(options.disambiguatingLine);

      seriousOptions = seriousOptions.orderBy((option) =>
        option
          .calculateAddedCutSegment()!
          .midpoint.projectOntoLine(options.disambiguatingLine)
          .distanceTo(projectedDisambiguatingPoint),
      );
    }

    return seriousOptions.first();
  }
}

export const handleReferenceLineClickDuringMemberCuttingMode =
  (targetMember: Member, clickedLine: CalculatedLine, clickPosition: Point2D): ThunkActionVoid =>
  (dispatch, getState) => {
    let workingComponentDesign = getActiveComponentDesign(getState());
    if (workingComponentDesign == null || targetMember.relativeGeometry == null) return;

    if (!workingComponentDesign.component.members.some((m) => m.guid === targetMember.guid)) {
      throw new Error('Attempt to cut a member that is not in the active component prevented.');
    }

    if (!workingComponentDesign.component.referenceLines?.some((line) => line.guid === clickedLine.guid)) {
      throw new Error('Attempt to cut a member with a line that is not in the active component prevented.');
    }

    const updatedMembers = new Map<string, Member>();

    function recordUpdatedMember(member: Member) {
      updatedMembers.set(member.guid, member);
      workingComponentDesign = produce(workingComponentDesign!, (draft) => {
        setMemberInComponentDesign(draft, member, false);
      });
    }

    const persistUpdatedMembers = (): ThunkActionVoid => (dispatch) => {
      dispatch(
        setMembers({
          members: Array.from(updatedMembers.values()),
          componentDesignGuid: workingComponentDesign!.guid,
        }),
      );

      dispatch(updateFeatures());
    };

    let possibilities;
    const startingMember = HypotheticalCutMember.representing(targetMember, workingComponentDesign.component);

    if (startingMember.isCutBy(clickedLine.guid)) {
      possibilities = startingMember.ifCutWasRemoved(clickedLine.guid, { from: 'both-sides' });
    } else {
      possibilities = startingMember.ifCutWasAdded(clickedLine.guid, { to: 'both-sides' });

      if (possibilities.none((option) => option.actuallyChangedShape())) {
        possibilities = multiplePotentialEdits(
          ...startingMember.getAllCuttingReferenceLineGuids().map((referenceLineGuid) => {
            return startingMember
              .ifCutWasRemoved(referenceLineGuid, { from: 'both-sides' })
              .andCutWasAdded(clickedLine.guid, { to: 'both-sides' });
          }),
        ).filter((option) => option.actuallyChangedShape());
      }
    }

    let bestOption = possibilities.pickBestOption({
      disambiguatingPoint: clickPosition,
      disambiguatingLine: calculateLine(clickedLine, workingComponentDesign.component)!,
    });

    if (bestOption?.actuallyChangedShape()) {
      bestOption = bestOption.garbageCollect();
      recordUpdatedMember(bestOption.member);
    } else {
      return;
    }

    // Experimental next step: detect members that now overlap the recently cut member and run auto-cutting on them.
    //
    // This is behind a feature flag for now because:
    //   - Client-side auto-cutting is experimental, has type errors, and is not yet fully tested.
    //   - The `overlaps` method used is incomplete and known to not work for all cases.
    //   - We aren't sure if this is actually a good feature to have, or if it will be useful in practice.
    if (hasAutoCutOnOverlapFeatureFlag(getState())) {
      let memberCutThisIteration;

      do {
        memberCutThisIteration = false;

        const modifiedMember = workingComponentDesign.component.members.find((m) => m.guid === targetMember.guid);
        if (modifiedMember == null) {
          throw new Error('A member vanished immediately after being cut, which is quite odd.');
        }

        const modifiedMemberShape = calculateRelativeMemberGeometry(
          modifiedMember.relativeGeometry!,
          workingComponentDesign.component,
        );
        if (modifiedMemberShape == null) break;

        const modifiedMemberPolygon = new Polygon2D(modifiedMemberShape.geometry);

        for (const member of workingComponentDesign.component.members) {
          if (member.relativeGeometry == null) continue;

          const memberShape = calculateRelativeMemberGeometry(
            member.relativeGeometry,
            workingComponentDesign.component,
          );
          if (memberShape == null) continue;

          try {
            const memberPolygon = new Polygon2D(memberShape.geometry);
            if (memberPolygon.overlaps(modifiedMemberPolygon)) {
              const autocutMember = {
                ...member,

                relativeGeometry: cutAgainst(
                  member.relativeGeometry,
                  workingComponentDesign.component,
                  memberShape.midline.midpoint,
                ),
              };

              recordUpdatedMember(autocutMember);

              memberCutThisIteration = true;
              break;
            }
          } catch (errors) {
            console.error(
              'Error thrown during experimental automatic auto-cut following edit. Auto-cutting will be halted.',
              errors,
            );

            break;
          }
        }
      } while (memberCutThisIteration);
    }

    dispatch(persistUpdatedMembers());
    dispatch(toggleMemberCuttingMode(false));
  };
